import { Footer } from "components/Footer/Footer";
import { Header } from "components/Header/Header";
import { Page } from "components/Page/Page";
import { DemoCard } from "components/ExtensionCard/DemoCard";
import { sharedExtensionCards } from "backend/src/common/gameData/extensions.shared";
import { ExtensionType } from "backend/src/common/types/ExtensionCard";
import { getExtensionTexts } from "backend/src/common/i18n";
import { Section } from "react-bulma-components";
import { useI18n } from "i18n/I18nProvider";
import { RULES_CARD_DETAIL_KEYS, RULES_CARD_GROUPS } from "i18n/content";
import { numberWithSign } from "utils/numberWithSign";

import styles from "./RulesPage.module.scss";

const getCardNotes = (
  type: ExtensionType,
  locale: "en" | "ru",
  t: ReturnType<typeof useI18n>["t"],
) => {
  const card = sharedExtensionCards[type];
  const notes: string[] = [];

  if (card.additionalFood) {
    notes.push(
      t("rules.note.foodDemand", {
        value: numberWithSign(card.additionalFood),
      }),
    );
  }
  if (card.optionalAdditionalFood) {
    notes.push(
      t("rules.note.optionalFood", {
        value: card.optionalAdditionalFood,
      }),
    );
  }
  if (card.shouldBePaired) {
    notes.push(t("rules.note.paired"));
  }
  if (card.canBeAddedToEnemyAnimal) {
    notes.push(t("rules.note.enemy"));
  }
  if (card.canBeAddeddMultipleTimes) {
    notes.push(t("rules.note.stackable"));
  }
  if (card.canBeInherited) {
    notes.push(t("rules.note.inheritable"));
  }
  if (card.canBeUsedOnStages?.length) {
    notes.push(t("rules.note.active"));
  }
  if (card.conflictsWith?.length) {
    const conflictNames = card.conflictsWith
      .map((conflict) => getExtensionTexts(locale, conflict).name)
      .join(", ");
    notes.push(
      t("rules.note.conflicts", {
        conflicts: conflictNames,
      }),
    );
  }

  return notes;
};

export const RulesPage = () => {
  const { locale, t } = useI18n();

  return (
    <>
      <Page withFooter>
        <Header title={t("rules.pageTitle")} />
        <Section>
          <div className={styles.layout}>
            <header className={styles.hero}>
              <p className={styles.eyebrow}>{t("rules.hero.eyebrow")}</p>
              <h1 className={styles.heroTitle}>{t("rules.hero.title")}</h1>
              <p className={styles.heroText}>{t("rules.hero.text")}</p>
            </header>

            <section className={styles.section}>
              <h2>{t("rules.goal.title")}</h2>
              <p className={styles.sectionText}>{t("rules.goal.text")}</p>
              <ul className={styles.list}>
                <li>{t("rules.goal.point.1")}</li>
                <li>{t("rules.goal.point.2")}</li>
                <li>{t("rules.goal.point.3")}</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>{t("rules.cards.title")}</h2>
              <p className={styles.sectionText}>{t("rules.cards.text")}</p>
              <ul className={styles.list}>
                <li>{t("rules.cards.point.1")}</li>
                <li>{t("rules.cards.point.2")}</li>
                <li>{t("rules.cards.point.3")}</li>
                <li>{t("rules.cards.point.4")}</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>{t("rules.placing.title")}</h2>
              <p className={styles.sectionText}>{t("rules.placing.text")}</p>
              <ul className={styles.list}>
                <li>{t("rules.placing.point.1")}</li>
                <li>{t("rules.placing.point.2")}</li>
                <li>{t("rules.placing.point.3")}</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>{t("rules.feeding.title")}</h2>
              <p className={styles.sectionText}>{t("rules.feeding.text")}</p>
              <ul className={styles.list}>
                <li>{t("rules.feeding.point.1")}</li>
                <li>{t("rules.feeding.point.2")}</li>
                <li>{t("rules.feeding.point.3")}</li>
                <li>{t("rules.feeding.point.4")}</li>
                <li>{t("rules.feeding.point.5")}</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>{t("rules.extinction.title")}</h2>
              <p className={styles.sectionText}>{t("rules.extinction.text")}</p>
              <ul className={styles.list}>
                <li>{t("rules.extinction.point.1")}</li>
                <li>{t("rules.extinction.point.2")}</li>
                <li>{t("rules.extinction.point.3")}</li>
                <li>{t("rules.extinction.point.4")}</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>{t("rules.catalogue.title")}</h2>
              <p className={styles.sectionText}>{t("rules.catalogue.text")}</p>

              <div className={styles.groupList}>
                {RULES_CARD_GROUPS.map((group) => (
                  <section key={group.titleKey} className={styles.cardGroup}>
                    <div className={styles.groupHeader}>
                      <h3>{t(group.titleKey)}</h3>
                      <p>{t(group.introKey)}</p>
                    </div>

                    <div className={styles.cardGrid}>
                      {group.cards.map((type) => {
                        const texts = getExtensionTexts(locale, type);
                        const notes = getCardNotes(type, locale, t);
                        const details = RULES_CARD_DETAIL_KEYS[type].map(
                          (key) => t(key),
                        );

                        return (
                          <article key={type} className={styles.cardEntry}>
                            <div className={styles.cardPreview}>
                              <DemoCard type={type} />
                            </div>

                            <div className={styles.cardBody}>
                              <div className={styles.cardHeader}>
                                <h4>{texts.name}</h4>
                                <p>{texts.description}</p>
                              </div>

                              {!!notes.length && (
                                <div className={styles.noteRow}>
                                  {notes.map((note) => (
                                    <span key={note} className={styles.note}>
                                      {note}
                                    </span>
                                  ))}
                                </div>
                              )}

                              <ul className={styles.detailList}>
                                {details.map((detail) => (
                                  <li key={detail}>{detail}</li>
                                ))}
                              </ul>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </section>
          </div>
        </Section>
      </Page>
      <Footer />
    </>
  );
};
